import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applySelectModeCursor,
  hideHoverOverlay,
  hideSelectionOverlay,
  removeAllOverlays,
  showHoverOverlay,
  showSelectionOverlay,
} from './overlays.js';

// ── Selección de elementos dentro del iframe de la vista previa ───────────────
//
// Adaptado de abi/screenshot-to-code (MIT), `PreviewComponent.tsx`.
//
// El punto no obvio: los listeners van en FASE DE CAPTURA sobre window. window
// es el primer nodo del recorrido del evento, así que corre antes que cualquier
// handler que la página haya registrado — incluidos los delegados en document.
//
// Sin esto, seleccionar el botón del formulario de contacto lo envía, tocar el
// acordeón de FAQ lo abre, y clickear el nav dispara el scroll. Nuestros
// templates traen los tres.

/**
 * Eventos que se ahogan para que el JS del template no dispare al seleccionar.
 *
 * OJO: ahogar 'mousedown' cancela TRES cosas del navegador de una sola vez —
 * empezar una selección por arrastre, poner el cursor donde se hizo clic, y el
 * doble clic para seleccionar una palabra. Por eso la supresión NO se aplica
 * dentro del elemento que se está editando: ahí el mouse tiene que comportarse
 * como en cualquier campo de texto.
 */
const SUPPRESSED_EVENTS = ['pointerdown', 'mousedown', 'mouseup', 'submit'] as const;

export interface IframeSelectionOptions {
  /** Modo activo. Con false, el hook no engancha nada y limpia lo que haya. */
  enabled: boolean;
  /** Solo se pueden seleccionar elementos con data-evo-id. */
  requireEvoId?: boolean;
  /** Se llama cuando el usuario selecciona un elemento (o lo deselecciona). */
  onSelect?: (el: HTMLElement | null) => void;
  /** Escape sale del modo. */
  onEscape?: () => void;
  /**
   * ¿Este elemento se puede editar? Decide cómo se dibuja la marca.
   * Sin esto todo se marcaría igual, incluidos los contenedores que no responden
   * al clic — el bug que se reportó dos veces.
   */
  isEditable?: (el: HTMLElement) => boolean;
  /**
   * Traduce el elemento resuelto al que realmente hay que tocar. Se aplica al
   * hover Y al clic, para que lo que se ve marcado sea lo que se va a editar.
   */
  /**
   * @param point coordenadas del evento dentro del iframe, cuando las hay. Sirven
   * para mirar qué más hay debajo del cursor: un placeholder de imagen dibuja un
   * ícono ENCIMA de la imagen, y sin el punto no hay forma de saberlo.
   */
  mapTarget?: (el: HTMLElement, point?: { x: number; y: number }) => HTMLElement;
  /**
   * Arranca la edición. Se llama en mousedown, ANTES de que el navegador procese
   * el clic, para que el cursor caiga donde se hizo clic y no al final del bloque.
   */
  onPromote?: (el: HTMLElement) => void;
}

export interface IframeSelectionApi {
  /** MutableRefObject para poder pasarlo directo al <iframe ref=...>. */
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  selectedElement: HTMLElement | null;
  clearSelection: () => void;
  /** Selecciona un elemento desde afuera (el breadcrumb, por ejemplo). */
  selectElement: (el: HTMLElement) => void;
  /**
   * outerHTML del elemento seleccionado. Limpio: los overlays viven fuera de
   * <body> y nunca tocan el elemento.
   */
  getSelectedHtml: () => string | null;
  /** data-evo-id del seleccionado, que es lo que ancla un comentario. */
  getSelectedEvoId: () => string | null;
}

/** Sube por el árbol hasta el ancestro más cercano que tenga data-evo-id. */
function resolveTarget(raw: HTMLElement | null, requireEvoId: boolean): HTMLElement | null {
  if (!raw) return null;
  if (!requireEvoId) return raw;
  const withId = raw.closest('[data-evo-id]');
  return (withId as HTMLElement | null) ?? null;
}

export function useIframeSelection(opts: IframeSelectionOptions): IframeSelectionApi {
  const { enabled, requireEvoId = true, onSelect, onEscape, isEditable, mapTarget, onPromote } = opts;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);

  // Los listeners se registran una vez y leen el modo de un ref: si dependieran
  // del estado, cada toggle los desengancharía y volvería a enganchar, y en el
  // medio se perdería algún evento.
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const selectedRef = useRef<HTMLElement | null>(null);
  useEffect(() => { selectedRef.current = selectedElement; }, [selectedElement]);

  const hoveredRef = useRef<HTMLElement | null>(null);

  // En un ref para que los listeners, que se registran una sola vez, siempre
  // consulten la versión actual.
  const isEditableRef = useRef(isEditable);
  useEffect(() => { isEditableRef.current = isEditable; }, [isEditable]);
  const mapTargetRef = useRef(mapTarget);
  useEffect(() => { mapTargetRef.current = mapTarget; }, [mapTarget]);
  const onPromoteRef = useRef(onPromote);
  useEffect(() => { onPromoteRef.current = onPromote; }, [onPromote]);

  /**
   * ¿El evento ocurrió dentro de algo que se está editando?
   *
   * Se pregunta al DOM, no a un ref del lado de React. Un ref hay que mantenerlo
   * sincronizado y el estado de React llega un render tarde; en medio de un
   * arrastre eso significaba leer null, suprimir el mouseup y cancelar la
   * selección. El atributo contenteditable ya es la respuesta, y está en el mismo
   * documento donde ocurre el evento.
   *
   * La comprobación es por forma y no con `instanceof Node`: el evento nace en el
   * documento del iframe, que tiene su propia jerarquía de clases, y comparado
   * contra el Node de ESTA ventana da false siempre. Con eso la supresión se
   * aplicaba también adentro del texto que se está editando, que es justo el
   * único lugar donde no debe aplicarse.
   */
  const dentroDeLaEdicion = useCallback((target: EventTarget | null): boolean => {
    const nodo = target as Node | null;
    if (!nodo || typeof nodo.nodeType !== 'number') return false;
    const el = nodo.nodeType === 1 ? (nodo as Element) : nodo.parentElement;
    return !!el?.closest?.('[contenteditable="true"], [contenteditable="plaintext-only"]');
  }, []);

  /** Sube al ancestro direccionable y después aplica la redirección. */
  const targetFrom = useCallback((
    raw: HTMLElement | null,
    point?: { x: number; y: number },
  ): HTMLElement | null => {
    const base = resolveTarget(raw, requireEvoId);
    if (!base) return null;
    return mapTargetRef.current?.(base, point) ?? base;
  }, [requireEvoId]);
  const stateOf = useCallback((el: HTMLElement): 'editable' | 'locked' =>
    isEditableRef.current?.(el) === false ? 'locked' : 'editable', []);

  const select = useCallback((el: HTMLElement | null) => {
    setSelectedElement(el);
    onSelect?.(el);
  }, [onSelect]);

  const handleClick = useCallback((event: MouseEvent) => {
    if (!enabledRef.current) return;

    if (dentroDeLaEdicion(event.target)) {
      // Se está clickeando adentro de lo que se edita: el navegador ya colocó el
      // cursor. Lo único que hay que frenar es que un link navegue.
      const anchor = (event.target as HTMLElement | null)?.closest?.('a');
      if (anchor) event.preventDefault();
      // El evento no se cancela, pero la selección se publica igual: la edición
      // la abre el mousedown, así que el click siguiente cae SIEMPRE adentro y
      // sin esto el panel nunca se enteraba de qué se había seleccionado.
      // Se publica el bloque que se está editando, no lo que haya debajo del
      // cursor, que puede ser un <strong> de adentro.
      const enEdicion = (event.target as HTMLElement | null)
        ?.closest?.('[contenteditable="true"], [contenteditable="plaintext-only"]');
      if (enEdicion && enEdicion !== selectedRef.current) {
        select(enEdicion as HTMLElement);
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const target = targetFrom(event.target as HTMLElement | null,
      { x: event.clientX, y: event.clientY });
    if (!target) return;

    hoveredRef.current = null;
    hideHoverOverlay(target.ownerDocument);
    select(target);
  }, [targetFrom, select, dentroDeLaEdicion]);

  const handleSuppressed = useCallback((event: Event) => {
    if (!enabledRef.current) return;

    // Dentro de lo que se está editando el mouse es del navegador: sin esto no se
    // puede arrastrar para seleccionar, ni poner el cursor donde uno quiere, ni
    // hacer doble clic para tomar una palabra.
    if (dentroDeLaEdicion(event.target)) return;

    // En un mousedown sobre algo editable se arranca la edición ANTES de que el
    // navegador procese el evento, y NO se cancela: así el propio navegador pone
    // el cursor en el punto exacto donde se hizo clic.
    if (event.type === 'mousedown' || event.type === 'pointerdown') {
      const target = targetFrom(event.target as HTMLElement | null);
      if (target && isEditableRef.current?.(target) !== false) {
        onPromoteRef.current?.(target);
        return;
      }
    }

    event.preventDefault();
    event.stopPropagation();
  }, [dentroDeLaEdicion, targetFrom]);

  const handleMouseOver = useCallback((event: MouseEvent) => {
    if (!enabledRef.current) return;
    const target = targetFrom(event.target as HTMLElement | null,
      { x: event.clientX, y: event.clientY });
    if (!target?.getBoundingClientRect) return;

    // El seleccionado manda sobre su espacio: no apilar el hover encima, que
    // tapa el anillo más fuerte.
    const selected = selectedRef.current;
    if (selected && (target === selected || selected.contains(target))) {
      hoveredRef.current = null;
      hideHoverOverlay(target.ownerDocument);
      return;
    }

    hoveredRef.current = target;
    showHoverOverlay(target, stateOf(target));
  }, [targetFrom, stateOf]);

  const handleMouseOut = useCallback((event: MouseEvent) => {
    if (!enabledRef.current) return;
    // Solo cuando el puntero sale del iframe entero.
    if (event.relatedTarget) return;
    hoveredRef.current = null;
    hideHoverOverlay((event.target as HTMLElement | null)?.ownerDocument);
  }, []);

  // Los anillos tienen que seguir pegados al elemento cuando la página scrollea
  // o se redimensiona con el cursor quieto.
  const handleReposition = useCallback(() => {
    if (!enabledRef.current) return;
    const hovered = hoveredRef.current;
    if (hovered?.isConnected) showHoverOverlay(hovered, stateOf(hovered));
    const selected = selectedRef.current;
    if (selected?.isConnected) showSelectionOverlay(selected, stateOf(selected));
  }, [stateOf]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabledRef.current) return;
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    onEscape?.();
  }, [onEscape]);

  // Los links internos (#ancla) navegan aunque el click esté ahogado.
  const handleLinkClick = useCallback((event: MouseEvent) => {
    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (href?.startsWith('#')) event.preventDefault();
  }, []);

  // ── Enganche de listeners ──────────────────────────────────────────────────
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const attach = (): void => {
      const win = iframe.contentWindow;
      if (!win) return;
      // Fase de captura sobre window: corre antes que cualquier handler que la
      // página haya registrado, incluidos los delegados en document.
      win.addEventListener('click', handleClick, true);
      for (const type of SUPPRESSED_EVENTS) {
        win.addEventListener(type, handleSuppressed, true);
      }
      win.addEventListener('mouseover', handleMouseOver, true);
      win.addEventListener('mouseout', handleMouseOut, true);
      win.addEventListener('scroll', handleReposition, true);
      win.addEventListener('resize', handleReposition);
      win.addEventListener('keydown', handleKeyDown, true);
      win.document.addEventListener('click', handleLinkClick);

      // Un reload reemplaza el documento: hay que re-aplicar el modo.
      if (enabledRef.current) applySelectModeCursor(win.document);
    };

    iframe.addEventListener('load', attach);
    // El documento puede estar ya cargado (el componente re-renderizó después
    // del load). addEventListener deduplica handlers idénticos, así que es seguro.
    if (iframe.contentWindow?.document.readyState === 'complete') attach();

    return () => {
      iframe.removeEventListener('load', attach);
      const win = iframe.contentWindow;
      if (!win) return;
      win.removeEventListener('click', handleClick, true);
      for (const type of SUPPRESSED_EVENTS) {
        win.removeEventListener(type, handleSuppressed, true);
      }
      win.removeEventListener('mouseover', handleMouseOver, true);
      win.removeEventListener('mouseout', handleMouseOut, true);
      win.removeEventListener('scroll', handleReposition, true);
      win.removeEventListener('resize', handleReposition);
      win.removeEventListener('keydown', handleKeyDown, true);
      win.document.removeEventListener('click', handleLinkClick);
    };
  }, [
    handleClick, handleSuppressed, handleMouseOver, handleMouseOut,
    handleReposition, handleKeyDown, handleLinkClick,
  ]);

  // ── Efectos del modo ───────────────────────────────────────────────────────
  useEffect(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (enabled) {
      applySelectModeCursor(doc);
      return;
    }
    hoveredRef.current = null;
    removeAllOverlays(doc);
    if (selectedRef.current) select(null);
  }, [enabled, select]);

  // Dibuja el anillo del seleccionado, y lo borra cuando se limpia desde afuera.
  useEffect(() => {
    if (selectedElement?.isConnected) {
      showSelectionOverlay(selectedElement, stateOf(selectedElement));
      return;
    }
    hideSelectionOverlay(iframeRef.current?.contentWindow?.document);
  }, [selectedElement, stateOf]);

  const clearSelection = useCallback(() => {
    hideSelectionOverlay(iframeRef.current?.contentWindow?.document);
    select(null);
  }, [select]);

  const getSelectedHtml = useCallback(
    () => selectedRef.current?.outerHTML ?? null,
    [],
  );

  const getSelectedEvoId = useCallback(
    () => selectedRef.current?.getAttribute('data-evo-id') ?? null,
    [],
  );

  return { iframeRef, selectedElement, clearSelection, selectElement: select, getSelectedHtml, getSelectedEvoId };
}
